import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Button, Input, Textarea, Select, StatusBadge, MetricCard, Card, Tabs, CopyButton, EmptyState } from '../../components/core';
import { http } from '../../lib/api';
import type { TenantDashboardDto, AnalyticsDto, DailyPointDto } from '@livestreamx/shared-types';
import { NAV } from './nav';

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="text-muted text-xs">{hint}</div>}
    </div>
  );
}

export function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const d = await http.get<AnalyticsDto>('/api/tenant/analytics');
      setData(d);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useState(() => { void fetchData(); });

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="analytics-page">
        <h1 className="page-title">Analytics</h1>
        {error && <div className="alert danger">{error}</div>}
        {loading ? (
          <div className="flex items-center gap-2 mt-4"><span className="spinner" /><span className="text-muted">Loading analytics…</span></div>
        ) : data ? (
          <>
            <div className="stats-grid">
              <StatCard label="Total streaming minutes" value={data.totalStreamingMinutes.toLocaleString()} hint="All-time" />
              <StatCard label="Total sessions" value={data.totalSessions} hint="Distinct viewer sessions" />
              <StatCard label="Avg session length" value={`${data.avgSessionMinutes.toFixed(1)} min`} hint="Per session" />
              <StatCard label="Bandwidth used" value={`${data.bandwidthUsedGb.toLocaleString()} GB`} hint="Total egress" />
              <StatCard label="Peak bitrate" value={`${data.peakBitrateKbps} kbps`} hint="Highest recorded" />
              <StatCard label="Avg bitrate" value={`${data.avgBitrateKbps} kbps`} hint="Across all streams" />
              <StatCard label="Destination uptime" value={`${data.destinationUptimePct.toFixed(1)}%`} hint="Relay reliability" />
              <StatCard label="Destination failures" value={data.destinationFailures} hint="FFmpeg exits" />
            </div>
            <Card title="Daily activity (last 30 days)">
              {data.daily.length === 0 ? (
                <div className="empty-state"><h4>No data yet</h4><p>Streaming activity will appear here once you go live.</p></div>
              ) : (
                <div className="daily-chart">
                  {data.daily.map((d) => (
                    <div key={d.date} className="daily-bar">
                      <div className="daily-bar-fill" style={{ height: `${Math.min(100, (d.streamingMinutes / Math.max(...data.daily.map(p => p.streamingMinutes)) * 100) || 0)}%` }} />
                      <div className="daily-bar-label">{d.date.slice(5)}</div>
                      <div className="daily-bar-value text-muted text-xs">{d.streamingMinutes}m</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="daily-meta text-muted text-xs mt-1">Streaming minutes per day. Bandwidth and session counts available in the detailed API.</div>
            </Card>
          </>
        ) : (
          <div className="empty-state"><h4>No analytics data</h4><p>Start streaming to see analytics here.</p></div>
        )}
      </div>
    </AppLayout>
  );
}
